import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Box, Stack, Typography, useTheme } from '@mui/material';
import { Button } from '@repo/ui';

import { AuthenticChip } from 'components/UI/AuthenticChip';
import { AuthenticityDrawer } from 'components/pages/certificates/AuthenticityDrawer';
import { ProductImagePlaceholder } from '@repo/ui'
import { FadeInImage } from 'components/UI/FadeInImage';
import { Footer } from 'components/Footer';
import { photoScrimSx } from 'components/pages/certificates/productPage/photoScrimSx';
import { NavigationHeader } from 'components/pages/certificates/productPage/NavigationHeader';
import { PendingSaleClaimDrawer } from 'components/pages/certificates/pendingSale';
import { useMyPendingSales } from 'hooks/api/pendingOwnership';
import { ROUTES } from 'routes';
import { font, radius } from 'settings/tokens';
import { formatedDate, parseApiDate } from 'utils/date';
import { formatSku } from 'utils/formatSku';
import type { PendingSale } from 'types';

/**
 * VRG-455 — the page for something the buyer has bought but not yet claimed.
 *
 * ## Why this exists rather than a button on the list card
 *
 * The card used to carry "Scan to claim ownership" itself. On a two-column grid
 * that put a 40px button with a five-word label into a ~180px column, and the
 * label rendered clipped against the button's own edge. Widening the button or
 * shortening the copy would both have treated a placement problem as a styling
 * one: a card in a grid is a SUMMARY, and the list's job is to say what you own,
 * not to host the flow that changes it. Every other row in that grid opens a
 * page; this one now does too.
 *
 * ## The shape mirrors the certificate page ON PURPOSE
 *
 * Photograph, then title, then state. The buyer meets the same layout here that
 * they will keep after claiming, so the claim does not feel like arriving
 * somewhere new — it feels like the page filling in. That continuity is the
 * whole reason to copy the shape rather than invent a cleaner one for a
 * short-lived state.
 *
 * ## What is deliberately ABSENT, and it is most of the page
 *
 * No Manage, no Share, no Provenance / Scans / Activity, no owner block, no
 * kebab. Not hidden behind a permission check — genuinely not rendered, because
 * every one of them is a statement about a CERTIFICATE and no certificate exists
 * yet. `isOwnerView` on the real page gates actions on a record that is already
 * bound; here there is nothing to bind them to. Rendering them disabled would
 * promise a surface the scan has not created.
 *
 * ⚠️ Nothing item-specific may appear here — no tag, no serial, no per-item
 * media. Same constraint as the card and for the same reason: the design's
 * load-bearing property is that this is indistinguishable from an ordinary
 * incoming transfer, and the first item-level field is where that stops being
 * true. See `PendingSale`'s own docstring.
 */
export const PendingSalePage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { pendingSaleId } = useParams<{ pendingSaleId: string }>();

  const [claiming, setClaiming] = useState<PendingSale | null>(null);
  // The chip is TAPPABLE here exactly as it is on the certificate page — it is
  // the only thing on screen asserting the product's core claim, so it needs
  // somewhere to say what that claim means. Doubly so in the unverified state,
  // which reads as an accusation without an explanation behind it.
  const [openAuthenticity, setOpenAuthenticity] = useState(false);

  /*
   * Read from the LIST rather than fetching one record.
   *
   * There is no GET /pending-ownership/customer/:id, and adding one to render a
   * page reachable only from a list already in cache would be an endpoint that
   * exists for a navigation. React-query serves this from the same key the
   * certificates page populated, so arriving here is usually a cache read.
   */
  const { data, isLoading } = useMyPendingSales();
  const sale = useMemo(
    () => (data?.items ?? []).find(item => item.id === pendingSaleId) ?? null,
    [data?.items, pendingSaleId],
  );

  const goBack = () => navigate(ROUTES.CERTIFICATES);

  if (!sale) {
    /*
     * Covers the genuine miss AND the post-claim moment: resolving removes the
     * record from this list, so the page the buyer is standing on stops
     * existing. The drawer navigates to the new certificate on success, but a
     * refetch can land first — so this must read as neutral, never as an error.
     */
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100dvh' }}>
        <NavigationHeader onBack={goBack} solid title='Purchase' />
        <Stack spacing={2} sx={{ px: 2, py: 6, alignItems: 'center', textAlign: 'center' }}>
          <Typography sx={{ fontSize: 16, color: 'text.secondary' }}>
            {isLoading ? 'Loading your purchase…' : 'This purchase is no longer waiting to be claimed.'}
          </Typography>
          <Button variant='secondary' onClick={goBack}>
            Back to certificates
          </Button>
        </Stack>
        <Box sx={{ mt: 'auto' }}>
          <Footer />
        </Box>
      </Box>
    );
  }

  const image = sale.product?.images?.[0];
  const title = sale.product?.title || 'Your purchase';
  const sku = sale.product?.sku ? formatSku(sale.product.sku) : null;
  const purchasedDate = parseApiDate(sale.purchased_at);
  const purchased = purchasedDate ? formatedDate({ date: purchasedDate }) : null;
  const brand = sale.company?.name;

  return (
    /* A full-height flex COLUMN so the footer is pushed to the bottom of the
       page rather than sitting wherever the content happens to end. On a short
       purchase (no SKU, one-line title) the content stops around the middle of
       the screen, and "Powered by VERA" floating mid-page reads as part of the
       card above it rather than as the page's foot. `100dvh`, not `100vh`, so
       mobile browser chrome does not push it below the fold. */
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100dvh', pb: 6 }}>
      {/* `overlay`, so Back is the same liquid-glass control every other page
          floats on its photograph (NavigationHeader's `buttonSx` only takes the
          blur when `overlay && !solid`). Without it the arrow renders bare, which
          is what shipped first and read as a different app. */}
      <NavigationHeader overlay onBack={goBack} title={title} />

      {/* The photograph, full-bleed — structurally IDENTICAL to the certificate
          page's `mediaShotSx` / `mediaFrameSx` pair, which is what puts the chip
          in the same place rather than approximately there.

          The chip is a SIBLING of the image inside an outer that carries
          `pb: 16px`, so `bottom: 32` lands 16px above the image's edge. Nesting
          it INSIDE the image box (as this first shipped) makes the same 32 sit a
          full 32px up — 16 too high, which is exactly how it read. */}
      <Box sx={{ position: 'relative', pb: '16px' }}>
        <Box
          sx={{
            ...photoScrimSx,
            width: '100%',
            aspectRatio: '1 / 1',
            backgroundColor: theme.palette.grey[50],
            overflow: 'hidden',
          }}
        >
          {image ? (
            <FadeInImage
              src={image}
              alt={title}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            // The SHARED empty state — same plate and glyph the platform's
            // tables use. Previously a bare grey[50] box with nothing in it.
            <ProductImagePlaceholder glyphSize={40} />
          )}
        </Box>

        <Box sx={{ position: 'absolute', bottom: 32, left: 16, zIndex: 1 }}>
          <AuthenticChip
            verified={false}
            onClick={() => setOpenAuthenticity(true)}
            onImage
          />
        </Box>
      </Box>

      <Stack sx={{ px: 2, pt: 3 }} spacing={2}>
        {/* Identity is the object's NAME — same treatment as ProductPageHeader
            (font.title, weight 400) so the two pages read as one object. */}
        <Box>
          <Typography variant='h1' fontWeight={400} fontSize={font.title} lineHeight={1.2}>
            {title}
          </Typography>
          {sku && (
            <Typography sx={{ fontSize: 14, color: 'text.secondary', mt: 0.5 }}>{sku}</Typography>
          )}
        </Box>

        {/*
          THE STATE CARD, directly beneath the title.

          It carries the status, what the status means, and the one action that
          changes it — together, because separating them is what made the card
          version confusing: a badge on a summary tile said "Not verified yet"
          and offered no room to explain why or what to do.

          The button gets a full-width row of its own inside the card, which is
          the actual fix for the clipped label: the constraint was never the
          copy, it was a ~180px grid column.
        */}
        <Stack
          spacing={1.5}
          sx={{
            p: 2,
            // `${n}px`, NOT the bare number. MUI multiplies a numeric
            // `borderRadius` by `shape.borderRadius`, which this app resolves
            // from the tenant's Studio `border_radius` — so `radius.md` (12)
            // rendered ~96px and the card came out as a squircle. The token's
            // own docstring warns about this; it is the app's most repeated
            // styling trap.
            borderRadius: `${radius.md}px`,
            border: `1px solid ${theme.palette.grey[100]}`,
            backgroundColor: theme.palette.common.white,
          }}
        >
          <Typography sx={{ fontSize: 14, color: 'text.secondary', lineHeight: 1.5 }}>
            {brand ? `${brand} recorded this purchase` : 'This purchase was recorded'}
            {purchased ? ` on ${purchased}` : ''}. Scan the tag on your item to claim ownership.
          </Typography>

          <Typography sx={{ fontSize: 14, color: 'text.secondary', lineHeight: 1.5 }}>
            There is no deadline. Your purchase stays here until you scan it.
          </Typography>

          <Button variant='primary' sx={{ width: '100%' }} onClick={() => setClaiming(sale)}>
            Scan to claim ownership
          </Button>
        </Stack>
      </Stack>

      {/* "Powered by VERA", same as every other page. The brand can suppress it
          per-certificate via `hide_powered_by`, but there is no certificate here
          to read that from — so this takes the default. */}
      {/* `mt: 'auto'` is what pins it: the column above takes its natural
          height and this absorbs the remainder. */}
      <Box sx={{ mt: 'auto' }}>
        <Footer />
      </Box>

      <AuthenticityDrawer
        open={openAuthenticity}
        onClose={() => setOpenAuthenticity(false)}
        verified='unverified'
      />

      <PendingSaleClaimDrawer
        sale={claiming}
        open={Boolean(claiming)}
        onClose={() => setClaiming(null)}
      />
    </Box>
  );
};

export default PendingSalePage;
